import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Alert, FormField, FormLabel, Modal } from "@impero-com/ui";
import moment from "moment-timezone";

import { DatePicker } from "components/datePicker";
import { CommentField } from "components/modals/CommentField";
import { Footer } from "components/modals/Footer";
import { useModalComment } from "components/modals/useModalComment";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import {
    CALENDAR_DAY_FORMAT,
    CalendarDay,
    SelectionDueDates,
    dayInTimezone,
    formatDayLong,
    minSelectableDay,
    summarizeDueDates,
    timezoneAbbreviation,
} from "../utils/extendDueDate";

import * as theme from "./ControlActivityReopenModal.css";

interface ControlActivityReopenModalProps {
    open: boolean;
    loading: boolean;
    selectionCount: number;
    selectionDueDates: SelectionDueDates | null;
    singleActivityTitle: string | null;
    canPostponeDueDate: boolean;
    onConfirm: (comment: string, newDueDay: CalendarDay | null) => void;
    onClose: () => void;
}

export function ControlActivityReopenModal({
    open,
    loading,
    selectionCount,
    selectionDueDates,
    singleActivityTitle,
    canPostponeDueDate,
    onConfirm,
    onClose,
}: ControlActivityReopenModalProps) {
    const { t } = useTranslation();
    const timezoneName = useCurrentUserTimezone();
    const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
    const { comment, setComment, trimmed, isValid, isTooLong } =
        useModalComment({ open });

    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedDay(null);
        }
    }, [open]);

    if (!selectionDueDates) {
        return null;
    }

    const isSingle = selectionCount === 1;
    const latestDueDay = dayInTimezone(selectionDueDates.latest, timezoneName);
    const minDay = minSelectableDay(selectionDueDates.latest, timezoneName);
    const summary = summarizeDueDates(selectionDueDates, timezoneName);

    const selectionSummary = (() => {
        if (isSingle && singleActivityTitle !== null) {
            return t("ControlActivitiesList_Reopen_SingleActivitySummary", {
                title: singleActivityTitle,
                date: formatDayLong(latestDueDay),
            });
        }
        return summary.type === "single"
            ? t("ControlActivitiesList_ExtendDueDate_CurrentDueDate", {
                  date: summary.date,
              })
            : t("ControlActivitiesList_ExtendDueDate_CurrentDueDates", {
                  earliest: summary.earliest,
                  latest: summary.latest,
              });
    })();

    return (
        <Modal
            variant="content"
            open={open}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() => onConfirm(trimmed, selectedDay)}
                    confirmLabel={t("ControlResult_Reopen")}
                    loading={loading}
                    okDisabled={!isValid}
                />
            }
            onClose={onClose}
            title={t(
                isSingle
                    ? "ControlResult_ReopenSingle_ConfirmTitle"
                    : "ControlResult_Reopen_ConfirmTitle"
            )}
        >
            <div className={theme.body}>
                <div>
                    <div className={theme.selectionCount}>
                        {t(
                            "ControlActivitiesList_ExtendDueDate_SelectedActivities",
                            { count: selectionCount }
                        )}
                    </div>
                    <div className={theme.selectionSummary}>
                        {selectionSummary}
                    </div>
                </div>
                <p className={theme.bodyText}>
                    {t(
                        isSingle
                            ? "ControlActivitiesList_ReopenSingle_Body"
                            : "ControlActivitiesList_Reopen_Body"
                    )}
                </p>
                {canPostponeDueDate && (
                    <FormField>
                        <FormLabel>
                            {t("ControlActivitiesList_Reopen_NewDueDateLabel")}
                        </FormLabel>
                        <div className={theme.caption}>
                            {t(
                                isSingle
                                    ? "ControlActivitiesList_ReopenSingle_MinDateCaption"
                                    : "ControlActivitiesList_Reopen_MinDateCaption",
                                { date: formatDayLong(latestDueDay) }
                            )}
                        </div>
                        <DatePicker
                            className={theme.datePicker}
                            value={
                                selectedDay
                                    ? moment(selectedDay, CALENDAR_DAY_FORMAT)
                                    : null
                            }
                            onChange={(value) =>
                                setSelectedDay(
                                    value
                                        ? value.format(CALENDAR_DAY_FORMAT)
                                        : null
                                )
                            }
                            disabledDate={(current) =>
                                current.format(CALENDAR_DAY_FORMAT) < minDay
                            }
                            placeholder={t(
                                "ControlActivitiesList_ExtendDueDate_SelectDatePlaceholder"
                            )}
                        />
                    </FormField>
                )}
                <CommentField
                    value={comment}
                    onChange={setComment}
                    label={t("ControlActivitiesList_Reopen_CommentLabel")}
                    placeholder={t(
                        "ControlActivitiesList_Reopen_CommentPlaceholder"
                    )}
                    rows={2}
                    error={
                        isTooLong ? t("ControlResult_Comment_Error") : undefined
                    }
                />
                {canPostponeDueDate && (
                    <Alert
                        type="info"
                        showIcon
                        message={t(
                            "ControlActivitiesList_Reopen_TimezoneNoteTitle",
                            {
                                timezone: timezoneAbbreviation(
                                    selectedDay,
                                    timezoneName
                                ),
                            }
                        )}
                        description={t(
                            "ControlActivitiesList_ExtendDueDate_TimezoneNoteBody"
                        )}
                    />
                )}
            </div>
        </Modal>
    );
}
